import { CheckCircle2, ExternalLink, PackagePlus, Search, X } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@krp/ui-kit'

import '../../../shared/ui/onec.css'
import { CommandStatus } from '../../../shared/ui/CommandStatus'
import { InputField } from '../../../shared/ui/InputField'
import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import { useUnsavedGuard } from '../../../shared/ui/useUnsavedGuard'
import type { ProductCreateActions, ProductCreateVm } from '../model/types'

/**
 * Новый товар в КОНКРЕТНОЙ базе 1С. До создания человек видит похожие товары
 * этой базы (по наименованию) — дубль дешевле не завести, чем потом сливать.
 * Создание — одна команда с ключом идемпотентности; неизвестный исход
 * проверяется той же командой, а не повторным «Создать».
 */
export function ProductCreateScreen({ vm, actions }: { vm: ProductCreateVm; actions: ProductCreateActions }) {
  const [confirmClose, setConfirmClose] = useState(false)
  useUnsavedGuard((vm.dirty && vm.created === null) || vm.locked)
  const close = () => (vm.dirty && vm.created === null ? setConfirmClose(true) : actions.close())

  return (
    <section className="onec-screen" aria-labelledby="onec-create-title">
      <header className="onec-toolbar">
        <h2 id="onec-create-title">Новый товар 1С</h2>
        <Button variant="ghost" icon={<X size={16} aria-hidden="true" />} onClick={close}>
          Закрыть
        </Button>
      </header>

      {confirmClose && (
        <div className="onec-banner" role="alertdialog" aria-label="Несохранённые изменения">
          <div>
            <p>Товар не создан. Закрыть окно?</p>
            <div className="onec-actions">
              <Button onClick={() => setConfirmClose(false)}>Остаться</Button>
              <Button variant="primary" onClick={actions.close}>
                Закрыть, черновик оставить
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  actions.discard()
                  actions.close()
                }}
              >
                Закрыть и отменить
              </Button>
            </div>
          </div>
        </div>
      )}

      {vm.created !== null ? (
        <div className="onec-banner" role="status">
          <CheckCircle2 size={18} aria-hidden="true" />
          <div>
            <p>
              {vm.created.outcome === 'Created'
                ? 'Товар создан в 1С.'
                : 'Такой товар уже есть в этой базе — новый не создавался.'}
            </p>
            {vm.created.barcodeWarning !== null && <p>{vm.created.barcodeWarning}</p>}
            <div className="onec-actions">
              <Button
                variant="primary"
                icon={<ExternalLink size={16} aria-hidden="true" />}
                onClick={() => vm.created !== null && actions.open(vm.created.refKey)}
              >
                Открыть карточку
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <form
          className="onec-form"
          onSubmit={(event) => {
            event.preventDefault()
            if (vm.canCreate) actions.create()
          }}
        >
          <InputField
            label="Наименование"
            value={vm.form.name.value}
            onChange={(value) => actions.fieldChanged('name', value)}
            onBlur={actions.checkSimilar}
            disabled={vm.locked}
            autoFocus
          />
          <InputField
            label="Артикул"
            value={vm.form.article.value}
            onChange={(value) => actions.fieldChanged('article', value)}
            disabled={vm.locked}
          />
          <InputField
            label="Штрихкод"
            value={vm.form.barcode.value}
            inputMode="numeric"
            onChange={(value) => actions.fieldChanged('barcode', value)}
            disabled={vm.locked}
          />
          <div className="onec-actions">
            <Button icon={<Search size={16} aria-hidden="true" />} onClick={actions.checkSimilar} disabled={vm.locked}>
              Проверить похожие
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={<PackagePlus size={16} aria-hidden="true" />}
              disabled={!vm.canCreate}
            >
              Создать в 1С
            </Button>
          </div>
          {vm.createHint !== null && <p className="onec-field__note">{vm.createHint}</p>}
        </form>
      )}

      {vm.command !== null && vm.created === null && (
        <CommandStatus
          command={vm.command}
          onResend={() => vm.command !== null && actions.resend(vm.command.key)}
          onDismiss={() => vm.command !== null && actions.dismiss(vm.command.key)}
        />
      )}

      {vm.checkedName !== null && vm.created === null && (
        <section className="onec-section" aria-labelledby="onec-similar-title">
          <h3 id="onec-similar-title">Похожие в этой базе: «{vm.checkedName}»</h3>
          <OneCQueryState query={vm.similarQuery} emptyText="Похожих товаров нет" onRetry={actions.checkSimilar} />
          {vm.similar.length > 0 && (
            <ul className="onec-list">
              {vm.similar.map((row) => (
                <li key={row.refKey}>
                  <Button variant="ghost" onClick={() => actions.open(row.refKey)}>
                    {row.name}
                  </Button>
                  <span className="onec-list__meta">{row.sku !== null ? `арт. ${row.sku}` : 'без артикула'}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </section>
  )
}
